import _orderBy from 'lodash-es/orderBy';
import {
  addDays,
  addWeeks,
  formatDistanceStrict,
  getDay,
  subDays,
  subWeeks,
} from 'date-fns';
import setMinutes from './time/setMinutes';
import setHours from './time/setHours';
import getHours from './time/getHours';
import getMinutes from './time/getMinutes';
import { format } from './time/format';
import { CahierJournalView } from '@teetsh/app/src/containers/CahierJournal/types';
import { frenchDatePatternWithSlashesAndHours } from './time/datePatterns';
import { getDurationInHoursAndMinutes } from './time/getDurationInHoursAndMinutes';

export function setTimeAtAnotherDate(time: Date, date: Date) {
  const tt = setMinutes(date, getMinutes(time));
  return setHours(tt, getHours(time));
}

export function areTimeEquals(time1: Date, time2: Date): boolean {
  return (
    format(time1, 'fr-FR', frenchDatePatternWithSlashesAndHours) ===
    format(time2, 'fr-FR', frenchDatePatternWithSlashesAndHours)
  );
}

export function getDurationInHoursAndMinutesBetween(
  start: Date,
  end: Date,
): string {
  const nbOfMinutes = parseInt(
    formatDistanceStrict(start, end, { unit: 'minute' }).split(' ')[0],
    10,
  );
  return getDurationInHoursAndMinutes(nbOfMinutes);
}

export function getNormalizedWorkingDays(workingDays: number[]): number[] {
  return _orderBy(workingDays).map(d => (d + 1) % 7);
}

export function getNextDayBasedOnWorkingDays(
  currentDate: Date,
  workingDays: number[],
) {
  if (workingDays.length === 0) {
    return addDays(currentDate, 1);
  }
  const sortedWorkingDays = getNormalizedWorkingDays(workingDays);
  let result = addDays(currentDate, 1);
  while (!sortedWorkingDays.includes(getDay(result))) {
    result = addDays(result, 1);
  }
  return result;
}

export function getNextNthDayBasedOnWorkingDays(
  currentDate: Date,
  workingDays: number[],
  n: number,
) {
  let newDate = currentDate;
  let nCopy = n;
  while (nCopy > 0) {
    newDate = getNextDayBasedOnWorkingDays(newDate, workingDays);
    nCopy--;
  }
  return newDate;
}

export function getDayBasedOnWorkingDays(
  currentDate: Date,
  workingDays: number[],
) {
  return getNextDayBasedOnWorkingDays(subDays(currentDate, 1), workingDays);
}

export function getPreviousDayBasedOnWorkingDays(
  currentDate: Date,
  workingDays: number[],
): Date {
  if (workingDays.length === 0) {
    return subDays(currentDate, 1);
  }
  const sortedWorkingDays = getNormalizedWorkingDays(workingDays);
  let result = subDays(currentDate, 1);
  while (!sortedWorkingDays.includes(getDay(result))) {
    result = subDays(result, 1);
  }
  return result;
}

export function isFirstDayOfWorkingWeek(
  date: Date,
  workingDays: number[],
): boolean {
  const sortedWorkingDays = getNormalizedWorkingDays(workingDays);
  return getDay(date) === sortedWorkingDays[0];
}

export function isLastDayOfWorkingWeek(
  date: Date,
  workingDays: number[],
): boolean {
  const sortedWorkingDays = getNormalizedWorkingDays(workingDays);
  return getDay(date) === sortedWorkingDays[workingDays.length - 1];
}

export function getNextDayBasedOnViewAndWorkingDays(
  view: CahierJournalView,
  currentDate: Date,
  workingDays,
): Date {
  if (view === 'week') {
    return addWeeks(currentDate, 1);
  }
  return getNextDayBasedOnWorkingDays(currentDate, workingDays);
}

export function getPreviousDayBasedOnViewAndWorkingDays(
  view: CahierJournalView,
  currentDate: Date,
  workingDays,
): Date {
  if (view === 'week') {
    return subWeeks(currentDate, 1);
  }
  return getPreviousDayBasedOnWorkingDays(currentDate, workingDays);
}

export function getNearestDate(
  dates: Date[],
  currentDate: Date,
): Date | undefined {
  if (dates.length === 0) {
    return undefined;
  }
  return dates.reduce((nearest, date) => {
    const dateDiff = Math.abs(date.getTime() - currentDate.getTime());
    const nearestDiff = Math.abs(nearest.getTime() - currentDate.getTime());
    return dateDiff < nearestDiff ? date : nearest;
  });
}
